import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
    Box, Button, Card, CircularProgress, Dialog, DialogActions, DialogContent,
    DialogTitle, InputAdornment, MenuItem, Table, TableBody, TableCell,
    TableContainer, TableHead, TablePagination, TableRow, TextField, Typography,
} from '@material-ui/core';
import SearchIcon from '@material-ui/icons/Search';

import {
    listPartnerRequests, decidePartnerRequest, PARTNER_REQUEST_STATUS, PARTNER_REQUEST_DECISION,
    STATUS_LABEL, STATUS_COLOR, STATUS_FILTERS,
} from '../../services/partnerRequests.service';

/**
 * Partner Requests — the DQ admins' queue of what partner workspaces have asked
 * DQ for: a solution upgrade or a dashboard from "Unlock a capability", or the
 * COS Government Checklist's Request Access. One queue for every type; the
 * Type filter narrows it.
 *
 * Approve and Decline email the partner (RQ-03 / RQ-04). What approving does
 * beyond that depends on the type and comes from the API on each row:
 *   auto_grant true  → approval switches the access on itself (grant_label)
 *   auto_grant false → the admin sets it up by hand first (setup_hint), and
 *                      approving tells the partner it is ready
 *
 * Material-UI v4 (this app is not on @mui/material), so styling is `style=`
 * rather than `sx=`.
 */

const fmtDate = (v) => {
    if (!v) return '—';
    const d = new Date(v);
    return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString();
};

const StatusPill = ({ status }) => {
    const c = STATUS_COLOR[status] || { fg: '#555', bg: '#eee' };
    return (
        <span style={{
            display: 'inline-block', padding: '3px 10px', borderRadius: 12,
            fontSize: 12, fontWeight: 700, color: c.fg, background: c.bg,
            whiteSpace: 'nowrap',
        }}>
            {STATUS_LABEL[status] || String(status)}
        </span>
    );
};

const PartnerRequests = () => {
    const [rows, setRows] = useState([]);
    const [total, setTotal] = useState(0);
    const [types, setTypes] = useState([]);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(25);
    const [search, setSearch] = useState('');
    // Pending first — that is the work.
    const [status, setStatus] = useState(String(PARTNER_REQUEST_STATUS.PENDING));
    const [requestType, setRequestType] = useState('');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    // { request, decision } — the row being decided.
    const [pending, setPending] = useState(null);
    const [note, setNote] = useState('');
    const [saving, setSaving] = useState(false);
    const [saveError, setSaveError] = useState('');

    /** Guards against an out-of-order response overwriting a newer one. */
    const reqId = useRef(0);

    const load = useCallback(async (params) => {
        const mine = ++reqId.current;
        setLoading(true);
        setError('');
        try {
            const res = await listPartnerRequests(params);
            if (mine !== reqId.current) return;
            if (res && res.data) {
                setRows(res.data.requests || []);
                setTotal(Number(res.data.total) || 0);
                if (Array.isArray(res.data.types)) setTypes(res.data.types);
            } else {
                setRows([]);
                setTotal(0);
                setError((res && res.message) || 'Could not load requests.');
            }
        } catch (e) {
            if (mine !== reqId.current) return;
            setRows([]);
            setTotal(0);
            setError((e && e.message) || 'Could not load requests.');
        } finally {
            if (mine === reqId.current) setLoading(false);
        }
    }, []);

    // Debounced: search runs on the server.
    useEffect(() => {
        const t = setTimeout(() => {
            load({ search, status, request_type: requestType, page, rows_per_page: rowsPerPage });
        }, 350);
        return () => clearTimeout(t);
    }, [search, status, requestType, page, rowsPerPage, load]);

    const reload = () => load({ search, status, request_type: requestType, page, rows_per_page: rowsPerPage });

    const resetPageWith = (setter) => (e) => { setter(e.target.value); setPage(0); };

    const onRowsPerPage = (e) => {
        setRowsPerPage(parseInt(e.target.value, 10));
        setPage(0);
    };

    const openDecision = (request, decision) => {
        setPending({ request, decision });
        setNote('');
        setSaveError('');
    };

    const confirmDecision = async () => {
        if (!pending) return;
        setSaving(true);
        setSaveError('');
        try {
            const res = await decidePartnerRequest({
                request_id: pending.request._id,
                decision: pending.decision,
                decision_note: note,
            });
            if (res && res.data) {
                setPending(null);
                reload();
            } else {
                setSaveError((res && res.message) || 'Could not save that decision.');
            }
        } catch (e) {
            setSaveError((e && e.message) || 'Could not save that decision.');
        } finally {
            setSaving(false);
        }
    };

    const decliningNow = pending && pending.decision === PARTNER_REQUEST_DECISION.DECLINE;
    const cannotConfirm = saving || (decliningNow && !note.trim());
    const req = pending && pending.request;

    const emptyMessage = search || requestType || status
        ? 'No requests match that filter.'
        : 'No partner has sent a request yet.';

    return (
        <Box style={{ padding: 24 }}>
            <Typography variant="h5" style={{ fontWeight: 700, marginBottom: 4 }}>
                Partner Requests
            </Typography>
            <Typography variant="body2" style={{ color: '#666', marginBottom: 20 }}>
                What partner workspaces have asked DQ for — solution upgrades and dashboards from
                &ldquo;Unlock a capability&rdquo;, and COS Government Checklist access. Approving or
                declining emails the partner.
            </Typography>

            <Card style={{ padding: 16 }}>
                <Box style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 16 }}>
                    <TextField
                        value={search}
                        onChange={resetPageWith(setSearch)}
                        placeholder="Search partner, requester, email or request"
                        variant="outlined"
                        size="small"
                        style={{ flex: '1 1 320px', maxWidth: 460 }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>
                            ),
                        }}
                    />
                    <TextField
                        select
                        value={requestType}
                        onChange={resetPageWith(setRequestType)}
                        label="Type"
                        variant="outlined"
                        size="small"
                        style={{ width: 300 }}
                    >
                        <MenuItem value="">All types</MenuItem>
                        {types.map((t) => (
                            <MenuItem key={t.key} value={t.key}>{t.label}</MenuItem>
                        ))}
                    </TextField>
                    <TextField
                        select
                        value={status}
                        onChange={resetPageWith(setStatus)}
                        label="Status"
                        variant="outlined"
                        size="small"
                        style={{ width: 180 }}
                    >
                        {STATUS_FILTERS.map((o) => (
                            <MenuItem key={o.value || 'all'} value={o.value}>{o.label}</MenuItem>
                        ))}
                    </TextField>
                </Box>

                {error ? (
                    <Typography style={{ color: '#b3261e', fontWeight: 600, padding: '12px 0' }}>
                        {error}
                    </Typography>
                ) : null}

                <TableContainer style={{ overflowX: 'auto' }}>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell style={{ fontWeight: 700 }}>Partner</TableCell>
                                <TableCell style={{ fontWeight: 700 }}>Request</TableCell>
                                <TableCell style={{ fontWeight: 700 }}>Details</TableCell>
                                <TableCell style={{ fontWeight: 700 }}>Requested by</TableCell>
                                <TableCell style={{ fontWeight: 700 }}>Requested on</TableCell>
                                <TableCell style={{ fontWeight: 700 }}>Status</TableCell>
                                <TableCell style={{ fontWeight: 700 }}>Action</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {loading ? (
                                <TableRow>
                                    <TableCell colSpan={7} align="center" style={{ padding: 40 }}>
                                        <CircularProgress size={26} />
                                    </TableCell>
                                </TableRow>
                            ) : null}

                            {!loading && rows.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={7} align="center" style={{ padding: 40, color: '#777' }}>
                                        {emptyMessage}
                                    </TableCell>
                                </TableRow>
                            ) : null}

                            {!loading && rows.map((r) => {
                                const open = Number(r.status) === PARTNER_REQUEST_STATUS.PENDING;
                                return (
                                    <TableRow key={r._id} hover>
                                        <TableCell>
                                            <div style={{ fontWeight: 700 }}>{r.partner_name || '—'}</div>
                                            <div style={{ fontSize: 11, color: '#888' }}>{r.dq_insider_id}</div>
                                        </TableCell>
                                        <TableCell style={{ fontWeight: 600 }}>{r.request_label || r.request_type}</TableCell>
                                        <TableCell style={{ maxWidth: 320 }}>
                                            {(r.details_list || []).length === 0 ? '—' : (r.details_list || []).map((d) => (
                                                <div key={d.label} style={{ fontSize: 12, marginBottom: 2 }}>
                                                    <span style={{ color: '#777' }}>{d.label}:</span> {d.value}
                                                </div>
                                            ))}
                                        </TableCell>
                                        <TableCell>
                                            <div>{r.requested_by_name || '—'}</div>
                                            <div style={{ fontSize: 12, color: '#777' }}>{r.requested_by_email || ''}</div>
                                        </TableCell>
                                        <TableCell style={{ whiteSpace: 'nowrap' }}>{fmtDate(r.createdAt)}</TableCell>
                                        <TableCell>
                                            <StatusPill status={Number(r.status)} />
                                            {/* The reason is the whole content of a decline — keep it by the status. */}
                                            {!open && r.decision_note ? (
                                                <div style={{ fontSize: 11, color: '#888', marginTop: 4, maxWidth: 220 }}>
                                                    {r.decision_note}
                                                </div>
                                            ) : null}
                                        </TableCell>
                                        <TableCell style={{ whiteSpace: 'nowrap' }}>
                                            {!open ? (
                                                <span style={{ color: '#999', fontSize: 12 }}>
                                                    Decided {fmtDate(r.decided_at)}
                                                    {r.decided_by ? <><br />by {r.decided_by}</> : null}
                                                </span>
                                            ) : (
                                                <>
                                                    <Button
                                                        size="small"
                                                        variant="outlined"
                                                        style={{ marginRight: 8, color: '#1b6b3a', borderColor: '#1b6b3a' }}
                                                        onClick={() => openDecision(r, PARTNER_REQUEST_DECISION.APPROVE)}
                                                    >
                                                        Approve
                                                    </Button>
                                                    <Button
                                                        size="small"
                                                        variant="outlined"
                                                        style={{ color: '#a33224', borderColor: '#a33224' }}
                                                        onClick={() => openDecision(r, PARTNER_REQUEST_DECISION.DECLINE)}
                                                    >
                                                        Decline
                                                    </Button>
                                                </>
                                            )}
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>

                {/* count comes from the SERVER — the table only ever holds one page. */}
                <TablePagination
                    component="div"
                    count={total}
                    page={page}
                    rowsPerPage={rowsPerPage}
                    rowsPerPageOptions={[10, 25, 50, 100]}
                    onChangePage={(e, p) => setPage(p)}
                    onPageChange={(e, p) => setPage(p)}
                    onChangeRowsPerPage={onRowsPerPage}
                    onRowsPerPageChange={onRowsPerPage}
                />
            </Card>

            <Dialog open={!!pending} onClose={() => (saving ? null : setPending(null))} maxWidth="sm" fullWidth>
                <DialogTitle>
                    {decliningNow ? 'Decline this request?' : 'Approve this request?'}
                </DialogTitle>
                <DialogContent>
                    <Typography variant="body2" style={{ fontWeight: 600, marginBottom: 12 }}>
                        {req ? `${req.partner_name || req.dq_insider_id} — ${req.request_label || req.request_type}` : ''}
                    </Typography>

                    {req && !decliningNow ? (
                        req.auto_grant ? (
                            <Typography variant="body2" style={{ color: '#555', marginBottom: 16 }}>
                                {req.grant_label} The partner&rsquo;s owner is signed out once so the change
                                takes effect, and is emailed that it is now available.
                            </Typography>
                        ) : (
                            <Typography variant="body2" style={{ color: '#8a5300', background: '#fff6e5', padding: 12, borderRadius: 6, marginBottom: 16 }}>
                                Approving does not change the partner&rsquo;s access. {req.setup_hint} The
                                partner is emailed that it is now available.
                            </Typography>
                        )
                    ) : null}
                    {decliningNow ? (
                        <Typography variant="body2" style={{ color: '#555', marginBottom: 16 }}>
                            The partner is emailed this reason. Their current access is unchanged, and they can ask again.
                        </Typography>
                    ) : null}

                    <TextField
                        value={note}
                        onChange={(e) => setNote(e.target.value)}
                        label={decliningNow ? 'Reason (required)' : 'Note (optional, internal)'}
                        variant="outlined"
                        size="small"
                        fullWidth
                        multiline
                        minRows={3}
                        required={!!decliningNow}
                    />

                    {saveError ? (
                        <Typography style={{ color: '#b3261e', fontWeight: 600, marginTop: 12 }}>
                            {saveError}
                        </Typography>
                    ) : null}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setPending(null)} disabled={saving}>Cancel</Button>
                    <Button
                        onClick={confirmDecision}
                        disabled={cannotConfirm}
                        variant="contained"
                        color="primary"
                    >
                        {saving ? 'Saving…' : (decliningNow ? 'Decline' : 'Approve')}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default PartnerRequests;
